import React, {useEffect, useMemo, useState} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import {Close} from '@mui/icons-material';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {useDispatch, useSelector} from 'react-redux';
import {appSelector} from '@app/store/selectors/appSelector';
import {savePobMasterDataCreator, fetchActiveSkuDataCreator, updateAverageSalePriceCreator} from './redux/slice';
import {pobReportStateSelector} from './redux/selectors';

interface SetASPDialogProps {
  open: boolean;
  onClose: () => void;
  divisionData: any[];
  editData?: any;
  selectedDivision?: any;
  onSaveSuccess?: () => void;
}

const labelStyle = {
  fontFamily: 'Poppins',
  fontSize: 12,
  fontWeight: 600,
  color: '#1c1939',
  marginBottom: 0.5,
};

/**
 * Dialog component for setting ASP.
 * Contains Division, Brand (SubBrand), Product (SKU) dropdowns,
 * Start Date, End Date calendars, and ASP input field.
 */
const SetASPDialog: React.FC<SetASPDialogProps> = ({
  open,
  onClose,
  divisionData,
  editData,
  selectedDivision: parentDivision,
  onSaveSuccess,
}) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(appSelector.getUserInfo());
  const activeSkuData = useSelector(
    pobReportStateSelector.getActiveSkuData(),
  );
  const [selectedDivision, setSelectedDivision] = useState<any>(null);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [asp, setAsp] = useState<string>('');
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  const isEditMode = !!editData;

  const brandOptions = useMemo(() => {
    const uniqueBrands = new Map();
    (activeSkuData ?? []).forEach((item: any) => {
      if (item.subBrand && !uniqueBrands.has(item.subBrand.id)) {
        uniqueBrands.set(item.subBrand.id, item.subBrand);
      }
    });
    return Array.from(uniqueBrands.values());
  }, [activeSkuData]);

  const productOptions = useMemo(() => {
    if (!selectedBrand) return activeSkuData ?? [];
    return (activeSkuData ?? []).filter(
      (item: any) => item.subBrand?.id === selectedBrand.id,
    );
  }, [selectedBrand, activeSkuData]);

  const resetForm = () => {
    setSelectedDivision(null);
    setSelectedBrand(null);
    setSelectedProduct(null);
    setStartDate(null);
    setEndDate(null);
    setAsp('');
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    if (open && isEditMode && editData) {
      const division = parentDivision ?? divisionData?.find(
        (d: any) => d.value === editData.divisionId,
      );
      setSelectedDivision(division || null);
      if (editData.subBrandId != null) {
        setSelectedBrand({id: editData.subBrandId, name: editData.subBrandName ?? ''});
      }
      if (editData.skuId != null) {
        setSelectedProduct({skuid: editData.skuId, name: editData.skuName ?? ''});
      }
      if (editData.divisionId) {
        dispatch(fetchActiveSkuDataCreator({divisionId: editData.divisionId}));
      }
      setAsp(editData.averageSalePrice != null ? String(editData.averageSalePrice) : '');
      setStartDate(editData.startDate ? editData.startDate : null);
      setEndDate(editData.endDate ? editData.endDate : null);
    }
    if (open && !isEditMode && parentDivision) {
      setSelectedDivision(parentDivision);
      dispatch(fetchActiveSkuDataCreator({divisionId: parentDivision.value}));
    }
  }, [open, divisionData, parentDivision]);

  useEffect(() => {
    if (isEditMode && editData && activeSkuData?.length > 0) {
      const brand = brandOptions.find(
        (b: any) => b.id === editData.subBrandId,
      );
      if (brand) setSelectedBrand(brand);
      const product = (activeSkuData ?? []).find(
        (item: any) => item.skuid === editData.skuId,
      );
      if (product) setSelectedProduct(product);
    }
  }, [activeSkuData, isEditMode]);

  const handleBrandChange = (_event: any, value: any) => {
    setSelectedBrand(value);
    setSelectedProduct(null);
  };

  const handleAspChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setAsp(value);
    }
  };

  const isSaveDisabled = isEditMode
    ? !startDate || !asp
    : !selectedDivision ||
      !selectedBrand ||
      !selectedProduct ||
      !startDate ||
      !asp;

  const handleSave = () => {
    if (isEditMode) {
      const requestBody = {
        id: editData.id,
        averageSalePrice: asp,
        startDate: startDate ? dayjs(startDate).format('YYYY-MM-DDT00:00:00.000') + 'Z' : '',
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DDT00:00:00.000') + 'Z' : '',
        modifiedBy: userInfo?.userId ?? 0,
        skuId: selectedProduct?.skuid ?? editData.skuId ?? 0,
        onSuccess: onSaveSuccess,
      };
      dispatch(updateAverageSalePriceCreator(requestBody));
    } else {
      const requestBody = {
        divisionId: selectedDivision?.value ?? 0,
        skuDetails: [
          {
            subBrandId: selectedBrand?.id ?? 0,
            skuId: selectedProduct?.skuid ?? 0,
            averageSalesPrice: asp,
            isDefaultSKU: false,
            startDate: startDate ? dayjs(startDate).format('YYYY-MM-DDT00:00:00.000') + 'Z' : '',
            endDate: endDate ? dayjs(endDate).format('YYYY-MM-DDT00:00:00.000') + 'Z' : '',
            createdBy: userInfo?.userId ?? 0,
          },
        ],
        onSuccess: onSaveSuccess,
      };
      dispatch(savePobMasterDataCreator(requestBody));
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={1}>
        <DialogTitle sx={{fontWeight: 600, fontSize: 18, color: '#322b7c'}}>
          {isEditMode ? 'Edit ASP' : 'Set ASP'}
        </DialogTitle>
        <IconButton
          onClick={onClose}
          data-testid="close-set-asp-dialog"
          color="primary">
          <Close />
        </IconButton>
      </Box>

      <DialogContent>
        <FormControl fullWidth sx={{marginBottom: 2}}>
          <Typography sx={labelStyle}>Division</Typography>
          <Autocomplete
            id="asp-division-dropdown"
            options={divisionData ?? []}
            size="small"
            value={selectedDivision}
            disabled={isEditMode || !!parentDivision}
            isOptionEqualToValue={(option: any, val: any) =>
              option?.value === val?.value
            }
            getOptionLabel={(option: any) => option?.label ?? ''}
            onChange={(_e, value) => {
              setSelectedDivision(value);
              setSelectedBrand(null);
              setSelectedProduct(null);
              setAsp('');
              if (value?.value) {
                dispatch(fetchActiveSkuDataCreator({divisionId: value.value}));
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                placeholder="Search Division"
              />
            )}
          />
        </FormControl>

        <FormControl fullWidth sx={{marginBottom: 2}}>
          <Typography sx={labelStyle}>Brand (Sub Brand)</Typography>
          <Autocomplete
            id="asp-brand-dropdown"
            options={brandOptions}
            size="small"
            value={selectedBrand}
            disabled={isEditMode}
            isOptionEqualToValue={(option: any, val: any) =>
              option?.id === val?.id
            }
            getOptionLabel={(option: any) => option?.name ?? ''}
            onChange={handleBrandChange}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                placeholder="Search Brand"
              />
            )}
          />
        </FormControl>

        <FormControl fullWidth sx={{marginBottom: 2}}>
          <Typography sx={labelStyle}>Product (SKU)</Typography>
          <Autocomplete
            id="asp-product-dropdown"
            options={productOptions}
            size="small"
            value={selectedProduct}
            disabled={isEditMode}
            isOptionEqualToValue={(option: any, value: any) => option?.skuid === value?.skuid}
            getOptionLabel={(option: any) =>
              option ? `${option.name}` : ''
            }
            onChange={(_e, value) => {
              setSelectedProduct(value);
              if (value?.AverageSalesPrice != null) {
                setAsp(String(value.AverageSalesPrice));
              } else {
                setAsp('');
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                placeholder="Search Product"
              />
            )}
          />
        </FormControl>

        <Box display="flex" gap={2} sx={{marginBottom: 2}}>
          <FormControl fullWidth>
            <Typography sx={labelStyle}>Start Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                open={startDateOpen}
                onOpen={() => setStartDateOpen(true)}
                onClose={() => setStartDateOpen(false)}
                value={startDate}
                inputFormat="DD/MM/YYYY"
                maxDate={endDate || undefined}
                onChange={newValue => setStartDate(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    size="small"
                    onClick={() => setStartDateOpen(true)}
                    inputProps={{
                      ...params.inputProps,
                      placeholder: 'Select Start Date',
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>

          <FormControl fullWidth>
            <Typography sx={labelStyle}>End Date (optional)</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                open={endDateOpen}
                onOpen={() => setEndDateOpen(true)}
                onClose={() => setEndDateOpen(false)}
                value={endDate}
                inputFormat="DD/MM/YYYY"
                minDate={startDate || undefined}
                onChange={newValue => setEndDate(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    size="small"
                    onClick={() => setEndDateOpen(true)}
                    inputProps={{
                      ...params.inputProps,
                      placeholder: 'Select End Date',
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>
        </Box>

        <FormControl fullWidth sx={{marginBottom: 2}}>
          <Typography sx={labelStyle}>ASP</Typography>
          <TextField
            id="asp-value-input"
            size="small"
            value={asp}
            onChange={handleAspChange}
            placeholder="Enter ASP value"
            inputProps={{'data-testid': 'asp-input'}}
          />
        </FormControl>
      </DialogContent>

      <DialogActions sx={{padding: '16px 24px'}}>
        <Button
          data-testid="cancel-set-asp"
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: '#322b7c',
            color: '#322b7c',
            textTransform: 'none',
            minWidth: 100,
          }}>
          Cancel
        </Button>
        <Button
          data-testid="save-set-asp"
          variant="contained"
          disabled={isSaveDisabled}
          onClick={handleSave}
          sx={{
            backgroundColor: '#322b7c',
            textTransform: 'none',
            minWidth: 100,
          }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetASPDialog;
